import type { TFunction } from 'i18next';
import { z } from 'zod';

import type { CatalogRunProductsPage, ScoreVerdict } from '../types/catalog-run-scores.api.types';
import type { RunScoreProductsPage } from '../types/content-run-scores.api.types';
import type {
  SignalMembershipCategory,
  SignalMembershipGroup,
  SignalMembershipWire,
} from '../types/performance-signals.api.types';
import type { FunnelProductMatrixItem } from '../types/run-funnel.api.types';
import type {
  ScorecardProductsGroup,
  ScorecardProductsKind,
  ScorecardProductsWire,
} from '../types/scorecard-products.api.types';

import { categoryLabel, criterionLabel, criterionReason, issueFieldKey } from './content-results';
import { DASH } from './run-format';

/** Scorecard row kinds (content-gen `scorecard/products`). */
export const SCORECARD_KINDS = [
  'FIELD',
  'CHECK',
] as const satisfies readonly ScorecardProductsKind[];
/** Performance-signal row kinds (content-gen membership read's `category`). */
export const SIGNAL_KINDS = [
  'OCCASION',
  'MACRO_TREND',
  'MICRO_TREND',
  'POPULAR_TERM',
  'POPULAR_ATTRIBUTE',
  'TOP_TREND',
] as const satisfies readonly SignalMembershipCategory[];
/** R13 — every scored product of the batch, before → after (filtered by verdict, not a row). */
export const SCORE_LIST_KIND = 'SCORES' as const;
/** Every Insights row kind that can list its products. */
export const INSIGHT_KINDS = [...SCORECARD_KINDS, ...SIGNAL_KINDS, SCORE_LIST_KIND] as const;
export type InsightKind = (typeof INSIGHT_KINDS)[number];

/** Every product group either read knows; `groupsForKind` says which apply to a row. */
export const INSIGHT_GROUPS = [
  'ALL',
  'IMPROVED',
  'SAME',
  'DROPPED',
  'BELOW',
  'NEWLY_ADDED',
  'CURRENTLY_SHOWN',
  'REMOVED',
] as const;
export type InsightGroup = (typeof INSIGHT_GROUPS)[number];

/** One Insights row whose products can be listed — the URL token and both reads key on this. */
export interface InsightTarget {
  kind: InsightKind;
  /** Attribute name (FIELD), criterion id (CHECK) or the signal row's label. */
  key: string;
  /** Category slug (category card FIELD rows) or a popular attribute's vertical; else null. */
  category: string | null;
}

/** True for the Performance-signals kinds (membership read) vs the scorecard kinds. */
export function isSignalKind(kind: InsightKind): kind is SignalMembershipCategory {
  return (SIGNAL_KINDS as readonly string[]).includes(kind);
}

/** The groups a row offers, in chip order: FIELD splits by movement, CHECK has none, signals
 *  split by membership change (popular attributes have no REMOVED group). */
export function groupsForKind(kind: InsightKind): readonly InsightGroup[] {
  if (kind === 'FIELD') return ['ALL', 'IMPROVED', 'SAME', 'DROPPED', 'BELOW'];
  if (kind === 'CHECK' || kind === SCORE_LIST_KIND) return ['ALL'];
  if (kind === 'POPULAR_ATTRIBUTE') return ['ALL', 'NEWLY_ADDED', 'CURRENTLY_SHOWN'];
  return ['ALL', 'NEWLY_ADDED', 'CURRENTLY_SHOWN', 'REMOVED'];
}

/** A URL `group` coerced to one this row offers — anything else (stale link) reads as `ALL`. */
export function normalizeGroup(kind: InsightKind, group: string | null | undefined): InsightGroup {
  const groups = groupsForKind(kind);
  return groups.find((g) => g === group) ?? 'ALL';
}

/** True when two targets name the same row (same kind, key and scope). */
export function sameTarget(a: InsightTarget | null, b: InsightTarget | null): boolean {
  return !!a && !!b && a.kind === b.kind && a.key === b.key && a.category === b.category;
}

/** The Insights URL's `open` token for a row — `FIELD:description` (its scope travels in `cat`). */
export function encodeOpenToken(target: InsightTarget): string {
  return `${target.kind}:${target.key}`;
}

/** Parses `open` (+ `cat`) back into a target; an unknown kind or empty key is ignored. */
export function parseOpenToken(
  open: string | undefined,
  cat: string | undefined,
): InsightTarget | null {
  if (!open) return null;
  const colon = open.indexOf(':');
  if (colon <= 0) return null;
  const kind = INSIGHT_KINDS.find((k) => k === open.slice(0, colon));
  const key = open.slice(colon + 1);
  if (!kind || !key) return null;
  return { kind, key, category: cat ?? null };
}

/** The Insights tab's URL state: the one expanded row (`open` + `cat`) and its group. (R14: an
 *  attribute row drills by `checkField` alone, so no per-row check choice lives here any more.) */
export const insightsSearchSchema = z.object({
  open: z.string().max(400).optional().catch(undefined),
  cat: z.string().max(200).optional().catch(undefined),
  group: z.enum(INSIGHT_GROUPS).optional().catch(undefined),
});
export type InsightsSearch = z.infer<typeof insightsSearchSchema>;

/** The Insights search that re-opens `target` (with `group` unless it's `ALL`). */
export function insightsOpenSearch(target: InsightTarget, group: InsightGroup): InsightsSearch {
  return {
    open: encodeOpenToken(target),
    ...(target.category ? { cat: target.category } : {}),
    ...(group !== 'ALL' ? { group } : {}),
  };
}

/** Products per page of the detail view (server paging). */
export const INSIGHT_DETAIL_PAGE_SIZE = 25;
/** Products the inline (minimal) view shows before "See all". */
export const INSIGHT_PREVIEW_SIZE = 5;

/** The verdict vocabulary the R13 products list filters on (before and after). */
export const SCORE_VERDICTS = ['PASS', 'REVIEW', 'FAIL'] as const satisfies readonly ScoreVerdict[];
/** The R13 products list's server orders offered on the detail page (`product` is not offered). */
export const SCORE_ORDERS = ['delta', 'post', 'pre'] as const;
export type ScoreListOrder = (typeof SCORE_ORDERS)[number];

/**
 * URL search of the detail route `/catalog-v2/runs/$runId/insights/products` — every field falls
 * back to a safe default on a bad value, so a stale or hand-edited link still lands somewhere.
 * `verdict` / `preVerdict` / `order` only apply to the R13 scored-products list (`kind=SCORES`).
 */
export const insightProductsSearchSchema = z.object({
  kind: z.enum(INSIGHT_KINDS).catch('FIELD'),
  key: z.string().max(400).catch(''),
  category: z.string().max(200).optional().catch(undefined),
  group: z.enum(INSIGHT_GROUPS).optional().catch(undefined),
  verdict: z.enum(SCORE_VERDICTS).optional().catch(undefined),
  preVerdict: z.enum(SCORE_VERDICTS).optional().catch(undefined),
  order: z.enum(SCORE_ORDERS).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
});
export type InsightProductsSearch = z.infer<typeof insightProductsSearchSchema>;

/** The detail route's search for a target (+ group), page 1. */
export function insightDetailSearch(
  target: InsightTarget,
  group: InsightGroup,
): InsightProductsSearch {
  return {
    kind: target.kind,
    key: target.key,
    ...(target.category ? { category: target.category } : {}),
    ...(group !== 'ALL' ? { group } : {}),
  };
}

/** R13 server filters of the scored-products list — verdict after / before and the order. */
export interface ScoreListFilter {
  verdict: ScoreVerdict | null;
  preVerdict: ScoreVerdict | null;
  order: ScoreListOrder;
}

/** The detail route's search for the scored-products list (`kind=SCORES`) under `filter`. */
export function scoreListSearch(filter: Partial<ScoreListFilter>): InsightProductsSearch {
  return {
    kind: SCORE_LIST_KIND,
    key: 'all',
    ...(filter.verdict ? { verdict: filter.verdict } : {}),
    ...(filter.preVerdict ? { preVerdict: filter.preVerdict } : {}),
    ...(filter.order && filter.order !== 'delta' ? { order: filter.order } : {}),
  };
}

/** A row's human label — "Description · Readability" for a check, the field's label for an
 *  attribute, the signal's own label otherwise. `fieldLabel` resolves field keys (useRunLabels). */
export function insightTargetLabel(
  target: Pick<InsightTarget, 'kind' | 'key'>,
  fieldLabel: (key: string) => string,
): string {
  if (target.kind === SCORE_LIST_KIND) return '';
  if (target.kind === 'FIELD') return fieldLabel(target.key);
  if (target.kind === 'CHECK') {
    const field = issueFieldKey(target.key);
    return field
      ? `${fieldLabel(field)} · ${criterionReason(target.key)}`
      : criterionLabel(target.key);
  }
  return target.key;
}

/** A category scope as words ("Health & beauty"), or null when unscoped. */
export function insightScopeLabel(target: InsightTarget): string | null {
  return target.category ? categoryLabel(target.category) : null;
}

/** One listed product, the same shape from either read (titles/thumbs joined for scorecard rows). */
export interface InsightProduct {
  /** Catalog product id — the batch product page's key; null when the read didn't send one. */
  productId: string | null;
  title: string | null;
  imageUrl: string | null;
  merchantId: string | null;
  before: number | null;
  after: number | null;
  priority: string | null;
  /** Checks the product still fails after enrichment (per-run / R13 scores reads); else null. */
  failingChecks: number | null;
  /** R13 list only: the verdict before / after (PASS | REVIEW | FAIL); absent elsewhere. */
  preTier?: string | null;
  postTier?: string | null;
}

/** Which read a page came from — the scorecard row read, content-gen's per-run scores, the signal
 *  membership read, or R13's catalog-run products (drives the panel's caption, filter, columns). */
export type InsightProductsSource = 'scorecard' | 'runScores' | 'membership' | 'catalogScores';

/** One page of a row's products, read-agnostic. `counts` is null when the row has no groups. */
export interface InsightProductsPage {
  items: InsightProduct[];
  /** Products in the selected group (the pager's total). */
  total: number;
  /** 1-based. */
  page: number;
  totalPages: number;
  counts: Partial<Record<InsightGroup, number>> | null;
  /** FIELD's "Below N" threshold; null elsewhere. */
  threshold: number | null;
  source: InsightProductsSource;
}

/** A scorecard row group → the read's lowercase `groups` key. */
const SCORECARD_GROUP_KEY = {
  ALL: 'all',
  IMPROVED: 'improved',
  SAME: 'same',
  DROPPED: 'dropped',
  BELOW: 'below',
} as const satisfies Record<ScorecardProductsGroup, string>;

/** The scorecard read's group for an Insights group (signal-only groups never reach it). */
export function toScorecardGroup(group: InsightGroup): ScorecardProductsGroup {
  return group in SCORECARD_GROUP_KEY ? (group as ScorecardProductsGroup) : 'ALL';
}

/** The membership read's group for an Insights group (scorecard-only groups never reach it). */
export function toMembershipGroup(group: InsightGroup): SignalMembershipGroup {
  return group === 'NEWLY_ADDED' || group === 'CURRENTLY_SHOWN' || group === 'REMOVED'
    ? group
    : 'ALL';
}

/** R8 — the ids on a page the read sent no title or image for (the ⑨ join's only work). */
export function idsNeedingInfo(
  items: readonly { productId: string; title?: string | null; imageUrl?: string | null }[],
): string[] {
  return items.filter((i) => !i.title || !i.imageUrl).map((i) => i.productId);
}

/** Adapter: a scorecard page → the shared page shape. R8: the read's own `title` / `imageUrl` /
 *  `externalId` win; the ⑨ title/thumb join only fills what it left null. */
export function fromScorecardPage(
  wire: ScorecardProductsWire,
  info: ReadonlyMap<string, FunnelProductMatrixItem>,
): InsightProductsPage {
  const g = wire.groups;
  return {
    items: wire.items.map((item) => {
      const row = info.get(item.productId);
      return {
        productId: item.productId,
        title: item.title ?? row?.title ?? null,
        imageUrl: item.imageUrl ?? row?.thumbUrl ?? row?.imageUrl ?? null,
        merchantId: item.externalId ?? null,
        before: item.before,
        after: item.after,
        priority: item.priority,
        failingChecks: null,
      };
    }),
    total: wire.pagination.total,
    page: wire.pagination.page,
    totalPages: wire.pagination.totalPages,
    counts: g
      ? {
          ALL: g.all,
          IMPROVED: g.improved,
          SAME: g.same,
          DROPPED: g.dropped,
          BELOW: g.below,
        }
      : null,
    threshold: wire.threshold,
    source: 'scorecard',
  };
}

/** Adapter: one membership page (0-based on the wire) → the shared page shape (1-based). */
export function fromMembershipPage(wire: SignalMembershipWire): InsightProductsPage {
  return {
    items: wire.products.content.map((p) => ({
      productId: p.productId,
      title: p.title,
      imageUrl: p.imageLink,
      merchantId: p.externalId,
      before: p.contentQualityBefore,
      after: p.contentQualityAfter,
      priority: null,
      failingChecks: null,
    })),
    total: wire.products.totalElements,
    page: wire.products.page + 1,
    totalPages: wire.products.totalPages,
    counts: { ...wire.counts },
    threshold: null,
    source: 'membership',
  };
}

/**
 * Adapter: one page of content-gen's per-run `scores/products` (the POST run ⇒ `post` is the
 * after score) + the before score joined from the catalog-keyed PRE read + the ⑨ title/thumb join.
 * A run that also carries its own `pre` keeps it; no groups (the read has none).
 */
export function fromRunScoresPage(
  wire: RunScoreProductsPage,
  before: ReadonlyMap<string, number | null>,
  info: ReadonlyMap<string, FunnelProductMatrixItem>,
): InsightProductsPage {
  return {
    items: wire.items.map((item) => {
      const row = info.get(item.productId);
      return {
        productId: item.productId,
        title: row?.title ?? null,
        imageUrl: row?.thumbUrl ?? row?.imageUrl ?? null,
        merchantId: null,
        before: item.pre ?? before.get(item.productId) ?? null,
        after: item.post,
        priority: null,
        failingChecks: item.failedChecksPost,
      };
    }),
    total: wire.total,
    page: wire.page,
    totalPages: wire.totalPages,
    counts: null,
    threshold: null,
    source: 'runScores',
  };
}

/** Adapter: one R13 products page → the shared page shape; identity from the read, ⑨ fallback. */
export function fromCatalogScoresPage(
  wire: CatalogRunProductsPage,
  info: ReadonlyMap<string, FunnelProductMatrixItem>,
): InsightProductsPage {
  return {
    items: wire.items.map((item) => {
      const row = info.get(item.productId);
      return {
        productId: item.productId,
        title: item.title ?? row?.title ?? null,
        imageUrl: item.imageUrl ?? row?.thumbUrl ?? row?.imageUrl ?? null,
        merchantId: item.externalId,
        before: item.pre,
        after: item.post,
        priority: null,
        failingChecks: item.failedChecksPost,
        preTier: item.preTier,
        postTier: item.postTier,
      };
    }),
    total: wire.total,
    page: wire.page,
    totalPages: wire.totalPages,
    counts: null,
    threshold: null,
    source: 'catalogScores',
  };
}

/** An empty page from the per-run read (an attribute with no check still failing after). */
export const EMPTY_RUN_SCORES_PAGE: InsightProductsPage = {
  items: [],
  total: 0,
  page: 1,
  totalPages: 0,
  counts: null,
  threshold: null,
  source: 'runScores',
};

/** A product's display title — catalog title, else its merchant id, else its id. */
export function insightProductTitle(p: InsightProduct): string {
  return p.title ?? p.merchantId ?? p.productId ?? DASH;
}

/** A score for display — whole numbers stay whole ("86"), fractions keep one decimal. */
export function formatInsightScore(value: number | null): string | null {
  if (value === null) return null;
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/** Why a row has no product list: a signal's membership isn't served; the category drill needs
 *  the scorecard read (not deployed yet); the per-run fallback needs the batch's after-enrichment
 *  score run (none recorded); the scored-products list needs content-gen's R13 read. */
export type InsightUnavailableReason = 'signal' | 'category' | 'noScores' | 'scores';

/** The caption naming what a per-run list is ("Products still failing a check on Description"). */
export function insightRunScoresCaption(
  t: TFunction<'catalog-v2-run'>,
  target: InsightTarget,
  label: string,
): string {
  return target.kind === 'CHECK'
    ? t('insightProducts.stillFailingCheck')
    : t('insightProducts.stillFailingOnField', { field: label });
}

/** The plain note for a row whose product list no read can serve (never an error). */
export function insightUnavailableText(
  t: TFunction<'catalog-v2-run'>,
  reason: InsightUnavailableReason | null,
): string {
  if (reason === 'signal') return t('insightProducts.signalUnavailable');
  if (reason === 'noScores') return t('insightProducts.noScoreRun');
  if (reason === 'scores') return t('insightProducts.scoresUnavailable');
  return t('insightProducts.categoryUnavailable');
}

/** The "no products" line — per-run lists say nothing still fails; groups say the group is empty. */
export function insightEmptyText(t: TFunction<'catalog-v2-run'>, data: InsightProductsPage | null) {
  return data?.source === 'runScores'
    ? t('insightProducts.noFailingProducts')
    : t('insightProducts.noProducts');
}
